// @flow
import * as React from 'react';
import type { MapStateToProps } from 'react-redux';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { formActions, mergeActionsToProps } from 'redux-pure-form';

import {
  applyTimezone,
  formatDate,
  getCustomFields,
  getParamsString,
  getRecurringSettings,
  getTimestamp
} from '../calendar.model.js';
import EventContainer from './calendar.event-container.js';
import Icon from '../../icon/icon.js';

import { dateFormatSettings, timeFormatSettings } from '../../settings/settings.consts.js';
import {
  AIR_EVENT_TYPE,
  defaultEventFormRecurring,
  EVENT_ADD,
  EVENT_CANCEL,
  EVENT_CHECKIN,
  EVENT_CHECKOUT,
  EVENT_EDIT,
  EVENT_RESOURCE,
  EVENT_VIEW,
  INSTRUCTORS_SCHEDULE,
  MAINTENANCE_EVENT_TYPE
} from '../calendar.consts.js';
import { LOAD_MANIFEST, WEIGHT_AND_BALANCE } from '../weight-and-balance/weight-and-balance.consts';

import { cancelCheckIn, cancelCheckOut, deleteEvent } from '../calendar-day.actions.js';
import { PrinterService } from '../../printer/printer.service';
import { getWeightAndBalanceManifest } from '../weight-and-balance/weight--and-balance-manifest/weight-and-balance-manifest.helpers';
import Indication from '../../ui/indication';
import { selectUIMessages } from '../calendar.selectors';
import { getFlightTime } from './utils/flight';

type OwnProps = {||};
type MappedState = {|
  UIMessages: Messages,
  event: event
|};

type Props = {|
  ...OwnProps,
  ...MappedState
|};

type State = {
  enableTrackFlight: boolean
};

export function Time({ timezone, globalTimezone, date, label, user }) {
  if (!date) {
    return null;
  }
  return (
    <div className="from-group">
      <label>{label}</label>{' '}
      <div className="form-control-static">
        {formatDate(
          date,
          timezone.offset,
          `${dateFormatSettings[user.settings.date]} ${timeFormatSettings[user.settings.time]}`
        )}
        <div className="text-muted">
          {formatDate(
            applyTimezone(date, globalTimezone.offset),
            0,
            `${dateFormatSettings[user.settings.date]} ${timeFormatSettings[user.settings.time]}`
          )}{' '}
          [ {globalTimezone.abbr} {globalTimezone.name} {globalTimezone.offsetString} ]
        </div>
      </div>
    </div>
  );
}

export function FlightTime({ time, label }) {
  return (
    <div className="from-group">
      <label>{label}</label>
      {': '}
      <span>{time || '00:00'}</span>
    </div>
  );
}

export function ShortTime({ timezone, date, label, user }) {
  if (!date) {
    return null;
  }
  return (
    <div className="from-group">
      <label>{label}</label>
      {': '}
      <span>
        {formatDate(
          date,
          timezone.offset,
          `${dateFormatSettings[user.settings.date]} ${timeFormatSettings[user.settings.time]}`
        )}
      </span>
    </div>
  );
}

class EventView extends React.Component<Props, State> {
  static footer = true;

  constructor(props: Props) {
    super(props);
    this.handleCheckOut = this.handleSwitchModals.bind(this, EVENT_CHECKOUT);
    this.handleCheckIn = this.handleSwitchModals.bind(this, EVENT_CHECKIN);
    this.handleResourceClick = this.handleSwitchModals.bind(this, EVENT_RESOURCE);
    this.handleCancel = this.handleSwitchModals.bind(this, EVENT_CANCEL);
    this.handleOpenWeightAndBalance = this.handleSwitchModals.bind(this, WEIGHT_AND_BALANCE);
    this.handleLoadManifest = this.handleLoadManifest.bind(this);

    this.handleButtonsClick = this.handleButtonsClick.bind(this);
    const { organization } = this.props;

    this.state = {
      enableTrackFlight: organization.organizationSettings.enableTrackFlight === '1'
    };
  }

  handleButtonsClick(e) {
    e.preventDefault();
    const { type } = e.currentTarget.dataset;
    const {
      cancelCheckOut,
      cancelCheckIn,
      customFieldsList,
      customFields,
      deleteEvent,
      event,
      fieldAttrs,
      filterForm,
      URL,
      user,
      userFilters,
      switchModals
    } = this.props;

    switch (type) {
      case 'delete':
      case 'delete-current':
      case 'delete-not-started':
      case 'delete-following':
      case 'delete-all':
        return confirm('Are you sure?') ? deleteEvent(type.split('delete')[1].slice(1)) : false;

      case 'cancel':
        return this.handleCancel(e);

      case 'cancel-checkout':
        return confirm('The event status will be changed. Do you want to continue?')
          ? cancelCheckOut()
          : false;

      case 'cancel-checkin':
        return confirm(
          `The entry will be removed from the meters logbook.${
            event.invoiceId
              ? '\nAfter canceling the check in and editing an event, the invoice will not be recalculated.' +
                '\nIt will have to be edited manually. Users will see it too if they cancel check-in.'
              : ''
          }\nDo you want to continue?`
        )
          ? cancelCheckIn()
          : false;

      case 'create-invoice':
        const url = `${URL.uri}/org/${event.organizationUnique}/invoices/create/event/${event.id}/`;

        return window.open(url, '_blank');

      case 'waiting':
        const timezone = event.timeZone[event.timeZoneID];
        fieldAttrs.onChange({
          eventForm: {
            ...event,
            globalStartTime: applyTimezone(event.startTime, timezone.offset),
            globalFinishTime: applyTimezone(event.finishTime, timezone.offset),
            pilotID: event.pilotID !== user.id ? user.id : '',
            resources: event.resources.map(res => res.id),
            timezone,
            params: getParamsString(filterForm, userFilters[filterForm.id]),
            customFields: getCustomFields(customFieldsList, customFields, event.organizationUnique),
            _timezone: timezone.id,
            id: '',
            addToWaiting: '1',
            ...defaultEventFormRecurring
          }
        });

        const eventWaitingParams = {
          ...URL.eventParams,
          action: EVENT_ADD,
          resource: event.resources[0] ? event.resources[0].id : event.instructorID,
          location: event.locationID
        };
        return switchModals(EVENT_ADD, eventWaitingParams, [eventWaitingParams]);

      case 'edit':
        const eventEditParams = {
          ...URL.eventParams,
          action: EVENT_EDIT,
          resource: event.resources[0] ? event.resources[0].id : event.instructorID,
          location: event.locationID
        };
        return switchModals(EVENT_EDIT, eventEditParams, [
          ...URL.eventBreadcrumbs,
          eventEditParams
        ]);
    }
  }

  handleSwitchModals(type, e) {
    const { URL, switchModals } = this.props;
    const { element, instructor, orgId, location } = e.currentTarget.dataset;
    const nextBreadcrumbs = URL.eventBreadcrumbs.concat({
      ...URL.eventParams,
      action: type,
      element
    });

    switchModals(
      type,
      { ...URL.eventParams, element, instructor, orgId, location },
      nextBreadcrumbs
    );
  }

  handleLoadManifest() {
    const { dispatch } = this.props;
    PrinterService.print(getWeightAndBalanceManifest(dispatch));
  }

  render() {
    const {
      UIMessages,
      event,
      accessgroups,
      organizationsAccess,
      user,
      customFields,
      activityTypes,
      resources,
      instructors
    } = this.props;
    const { enableTrackFlight } = this.state;

    if (!Object.keys(event).length) return null;

    const timezone = event.timeZone[event.timeZoneID];
    const globalTimezone = this.props.timezone;

    let checkCondition = false;
    const org = event.organizationUnique || {};
    const orgAccess = accessgroups.checkOutAircraft && organizationsAccess[org];
    const isMaintenanceEvent = event.type === MAINTENANCE_EVENT_TYPE;

    if (orgAccess) {
      const accessGroups = orgAccess.accessGroups || {};
      const userIsAPilot = event.pilotID === user.id;

      checkCondition =
        (accessGroups.calendarManageAnothers && !userIsAPilot && !isMaintenanceEvent) ||
        (accessGroups.calendarManageMy && userIsAPilot && !isMaintenanceEvent) ||
        (accessGroups.resourcesManage && accessGroups && isMaintenanceEvent);
    }
    const instructorUserID =
      instructors[`${event.locationID}-${event.instructorID}`] &&
      resources[instructors[`${event.locationID}-${event.instructorID}`].resourceID] &&
      resources[instructors[`${event.locationID}-${event.instructorID}`].resourceID].userID;

    const { weightAndBalanceEnable, seatsAmount, passengersAmount } = event;
    const activityType = event.activityType && activityTypes[event.activityType];
    const airEventType = event.type === AIR_EVENT_TYPE ? 'Regular ' : 'Maintenance ';

    const checkOutEnable = parseInt(event.checkOutEnable, 10) === 1;
    const checkInEnable = parseInt(event.checkInEnable, 10) === 1;
    const aircraftExists = parseInt(event.aircraftExists, 10) === 1;
    const checkOutError = parseInt(event.checkOutError, 10) === 1;
    const positionInQueue = parseInt(event.positionInQueue, 10);
    const isRecurringEvent = event.recurring === '1';

    let flightTime = getFlightTime(event, enableTrackFlight);

    return (
      <EventContainer>
        <div className="col-sm-5">
          <div className="from-group">
            <label>Organization</label>{' '}
            <p className="form-control-static">{event.organizationName}</p>
          </div>

          <div className="from-group">
            <label>Location</label>{' '}
            <p className="form-control-static">
              {event.locationName}{' '}
              <span className="text-muted">
                [ {timezone.abbr} {timezone.name} {timezone.offsetString} ]
              </span>
            </p>
          </div>

          <div className="from-group">
            <label>Event Type and Status</label>{' '}
            <p className="form-control-static">
              {event.reserve === '1' ? (
                positionInQueue ? (
                  <span>#{positionInQueue} In Queue</span>
                ) : (
                  <span>Deactivated</span>
                )
              ) : (
                <span>
                  {airEventType}
                  {aircraftExists && positionInQueue !== '1' && (
                    <span className="form-group">
                      {checkOutEnable && !checkInEnable && !checkOutError && (
                        <span>
                          Not Started{' '}
                          {checkCondition && (
                            <button
                              type="button"
                              className="btn btn-success"
                              data-element={event.aircraftID || event.simulatorID}
                              onClick={this.handleCheckOut}
                            >
                              <span className="icon plane-air" /> Check Out
                            </button>
                          )}
                        </span>
                      )}
                      {checkOutError && (
                        <span
                          className="form-control-static text-danger"
                          dangerouslySetInnerHTML={{ __html: event.checkOutErrorMessage }}
                        />
                      )}
                      {!checkOutEnable && checkInEnable && (
                        <span>
                          Checked Out{' '}
                          {checkCondition && (
                            <button
                              type="button"
                              className="btn btn-success"
                              data-element={event.aircraftID || event.simulatorID}
                              onClick={this.handleCheckIn}
                            >
                              <span className="icon plane-earth" /> Check In
                            </button>
                          )}
                        </span>
                      )}
                      {!checkOutEnable && !checkInEnable && <span>Completed</span>}
                    </span>
                  )}
                </span>
              )}
            </p>
          </div>

          {event.type === AIR_EVENT_TYPE && (
            <span>
              <div className="from-group">
                <label>Pilot</label>{' '}
                {accessgroups.userReview &&
                event.pilotID !== user.id &&
                event.pilotName !== 'Restricted' ? (
                  <a
                    className="underline"
                    title="Open user in new window"
                    href={`${window.serverData._uri}/org/${event.organizationUnique}/users/review/${event.pilotID}/`}
                    target="_blank"
                  >
                    {event.pilotName} {event.pilotMiddlename} {event.pilotSurname}
                    <span className="icon external-link" />
                  </a>
                ) : (
                  <p className="form-control-static">
                    {event.pilotName} {event.pilotMiddlename} {event.pilotSurname}{' '}
                    {event.pilotID === user.id && (
                      <span className="label label-default label-its-you">it's you</span>
                    )}
                  </p>
                )}
              </div>

              <div className="from-group">
                <label>SIC Pilot</label>{' '}
                {event.secondInComand === '1' ? (
                  accessgroups.userReview && event.secondInComandID !== user.id ? (
                    <a
                      className="underline"
                      title="Open user in new window"
                      href={`${window.serverData._uri}/org/${event.organizationUnique}/users/review/${event.secondInComandID}/`}
                      target="_blank"
                    >
                      {event.sicPilotName} {event.sicPilotMiddlename} {event.sicPilotSurname}{' '}
                      <span className="icon external-link" />
                    </a>
                  ) : (
                    <p className="form-control-static">
                      {event.sicPilotName} {event.sicPilotMiddlename} {event.sicPilotSurname}
                      {event.secondInComandID === user.id && (
                        <span className="label label-default label-its-you">it's you</span>
                      )}
                    </p>
                  )
                ) : (
                  <p className="form-control-static">None</p>
                )}
              </div>

              {
                <div className="from-group">
                  <label>Instructor</label>{' '}
                  {parseInt(event.instructorID, 10) > 0 ? (
                    organizationsAccess[event.organizationUnique] &&
                    (organizationsAccess[event.organizationUnique].accessGroups
                      .calendarManageAnothers ||
                      event.resourcesRestricted.indexOf(event.instructorID) === -1) ? (
                      <span>
                        {accessgroups.userReview &&
                        instructorUserID &&
                        instructorUserID !== user.id ? (
                          <a
                            className="underline"
                            title="Open user in new window"
                            href={`${window.serverData._uri}/org/${event.organizationUnique}/users/review/${instructorUserID}/`}
                            target="_blank"
                          >
                            {event.instructorName} {event.instructorMiddlename}{' '}
                            {event.instructorSurname} <span className="icon external-link" />
                          </a>
                        ) : (
                          <p className="form-control-static">
                            {event.instructorName} {event.instructorMiddlename}{' '}
                            {event.instructorSurname}
                            {instructorUserID === user.id && (
                              <span className="label label-default label-its-you">it's you</span>
                            )}
                          </p>
                        )}
                        {!checkOutEnable && !checkInEnable && aircraftExists && (
                          <span> {`(Total: ${event.resourcesDiff[event.instructorID]})`}</span>
                        )}
                      </span>
                    ) : (
                      <span>Restricted Resource</span>
                    )
                  ) : (
                    <p className="form-control-static">None</p>
                  )}
                </div>
              }

              {event.customFields &&
                Object.keys(event.customFields).length &&
                Object.keys(event.customFields).map(fieldID => (
                  <div key={fieldID} className="from-group">
                    <label>{customFields[fieldID].name}</label>{' '}
                    {event.customFields[fieldID] ? (
                      customFields[fieldID].type === 'date' ? (
                        <p className="form-control-static">
                          {formatDate(
                            getTimestamp(event.customFields[fieldID]),
                            0,
                            dateFormatSettings[user.settings.date]
                          )}
                        </p>
                      ) : (
                        <p className="form-control-static">{event.customFields[fieldID]}</p>
                      )
                    ) : (
                      <p className="form-control-static">None</p>
                    )}
                  </div>
                ))}

              {activityType && (
                <div className="from-group">
                  <label>Activity Type</label>
                  <div className="d-flex">
                    {activityType.name}
                    &nbsp;
                    <span
                      className="activity-type-preview"
                      style={{ backgroundColor: activityType.color }}
                    />
                  </div>
                </div>
              )}
            </span>
          )}
        </div>

        <div className="col-sm-7 event-col-right">
          <Time
            label="Start Time"
            user={user}
            timezone={timezone}
            globalTimezone={globalTimezone}
            date={event.startTime}
          />

          <Time
            label="End Time"
            user={user}
            timezone={timezone}
            globalTimezone={globalTimezone}
            date={event.finishTime}
          />
          {enableTrackFlight && !isMaintenanceEvent && (
            <div className="track-flight-form">
              <Time
                label="Departure Time"
                user={user}
                timezone={timezone}
                globalTimezone={globalTimezone}
                date={event.departureTime}
              />
              <FlightTime label="Flight Time" user={user} timezone={timezone} time={flightTime} />
              <Time
                label="Expected Arrival Time"
                user={user}
                timezone={timezone}
                globalTimezone={globalTimezone}
                date={event.expectedArrivalTime}
              />
              <Time
                label="Actual Arrival Time"
                user={user}
                timezone={timezone}
                globalTimezone={globalTimezone}
                date={event.actualArrivalTime}
              />
            </div>
          )}
          {isRecurringEvent &&
          organizationsAccess[event.organizationUnique] &&
          organizationsAccess[event.organizationUnique].accessGroups
            .calendarManageRecurrentEvent ? (
            <div className="from-group">
              <div className="from-group">
                <input id="recurring" checked={isRecurringEvent} type="checkbox" disabled />{' '}
                <label htmlFor="recurring">Recurring Event</label>
              </div>
              <div className="help-block clean">
                <b>{getRecurringSettings(event, timezone.offset)}</b>
              </div>
            </div>
          ) : null}

          {event.resources.length ? (
            <div className="from-group">
              <label htmlFor="recurring">Resources</label>
              {event.resources.map(resource => {
                const isAirworthy = resource.statusInspections === 'airworthy';
                const isUnairworthy = resource.statusInspections === 'unairworthy';

                return !(
                  event.resourcesRestricted.indexOf(resource.id) > -1 &&
                  organizationsAccess[event.organizationUnique] &&
                  !organizationsAccess[event.organizationUnique].accessGroups.calendarManageAnothers
                ) ? (
                  <div key={resource.id}>
                    <b>{resource.typeName}:</b>{' '}
                    {resource.aircraftID !== null || resource.simulatorID !== null ? (
                      <span
                        data-element={resource.aircraftID || resource.simulatorID}
                        onClick={this.handleResourceClick}
                      >
                        <span
                          className="link--ajax"
                          title={isAirworthy ? 'Airworthy' : isUnairworthy ? 'Unairworthy' : ''}
                        >
                          {resource.name}
                          {accessgroups.discrepanciesView && resource.unique === 'aircraft' ? (
                            <span>{` (Discrepancies: ${resource.discrepanciesCount})`}</span>
                          ) : null}
                        </span>
                        <span
                          className={`inspections-status icon
                                    ${isAirworthy ? ' checkmark-circle text-success' : ''}
                                    ${isUnairworthy ? ' cancel-circled text-danger' : ''}
                                  `}
                          title={isAirworthy ? 'Airworthy' : isUnairworthy ? 'Unairworthy' : ''}
                        />
                      </span>
                    ) : (
                      <span>{resource.name}</span>
                    )}
                  </div>
                ) : event.resourcesRestricted.indexOf(resource.id) > -1 &&
                  (organizationsAccess[event.organizationUnique] &&
                    !organizationsAccess[event.organizationUnique].accessGroups
                      .calendarManageAnothers) ? (
                  <div>Restricted Resource</div>
                ) : null;
              })}
            </div>
          ) : null}

          <div className="from-group">
            <label>Comments</label>{' '}
            <p className="form-control-static">{event.comments || 'None'}</p>
          </div>

          {weightAndBalanceEnable && !isMaintenanceEvent && (
            <div className="from-group">
              <span
                id="manage-pass-btn"
                onClick={this.handleOpenWeightAndBalance}
                className="d-flex a-center"
              >
                <Icon width="13px" height="13px" name="team" className="m-r-6" />
                <span className="link--ajax">
                  {`Manage ${
                    seatsAmount ? `passengers (${passengersAmount}/${seatsAmount}) & ` : ''
                  }Weight and Balance`}
                </span>
              </span>
            </div>
          )}
        </div>

        <Indication className="event-form-indication" messages={UIMessages} />

        {/*   // ---------- BUTTONS ---------- //   */}
        <input
          id={`${EVENT_VIEW}-delete`}
          data-type="delete"
          onClick={this.handleButtonsClick}
          style={{ display: 'none' }}
        />
        <input
          id={`${EVENT_VIEW}-delete-current`}
          data-type="delete-current"
          onClick={this.handleButtonsClick}
          style={{ display: 'none' }}
        />
        <input
          id={`${EVENT_VIEW}-delete-not-started`}
          data-type="delete-not-started"
          onClick={this.handleButtonsClick}
          style={{ display: 'none' }}
        />
        <input
          id={`${EVENT_VIEW}-delete-following`}
          data-type="delete-following"
          onClick={this.handleButtonsClick}
          style={{ display: 'none' }}
        />
        <input
          id={`${EVENT_VIEW}-delete-all`}
          data-type="delete-all"
          onClick={this.handleButtonsClick}
          style={{ display: 'none' }}
        />

        <input
          id={`${EVENT_VIEW}-cancel`}
          data-type="cancel"
          onClick={this.handleButtonsClick}
          style={{ display: 'none' }}
        />
        <input
          id={`${EVENT_VIEW}-cancel-checkout`}
          data-type="cancel-checkout"
          onClick={this.handleButtonsClick}
          style={{ display: 'none' }}
        />
        <input
          id={`${EVENT_VIEW}-cancel-checkin`}
          data-type="cancel-checkin"
          onClick={this.handleButtonsClick}
          style={{ display: 'none' }}
        />

        <input
          id={`${EVENT_VIEW}-create-invoice`}
          data-type="create-invoice"
          onClick={this.handleButtonsClick}
          style={{ display: 'none' }}
        />

        <input
          id={`${EVENT_VIEW}-waiting`}
          data-type="waiting"
          onClick={this.handleButtonsClick}
          style={{ display: 'none' }}
        />
        <input
          id={`${EVENT_VIEW}-edit`}
          data-type="edit"
          onClick={this.handleButtonsClick}
          style={{ display: 'none' }}
        />

        <input
          id={LOAD_MANIFEST}
          onClick={this.handleLoadManifest}
          type="button"
          style={{ display: 'none' }}
        />
      </EventContainer>
    );
  }
}

const mapStateToProps: MapStateToProps<ReduxState, OwnProps, MappedState> = state => ({
  UIMessages: selectUIMessages(EVENT_VIEW)(state),
  URL: state.URL,
  event: state.event,
  filterForm: state.filterForm,
  userFilters: state.userFilters,
  timezone: state.timezone,
  accessgroups: state.accessgroups,
  organization: state.organization,
  organizationsAccess: state.organizationsAccess,
  user: state.user,
  customFields: state.customFields,
  activityTypes: state.activityTypes,
  resources: state.resources,
  instructors: state.instructors,
  customFieldsList: state.customFieldsList
});

const mapDispatchToProps = dispatch => ({
  dispatch,
  ...bindActionCreators({ deleteEvent, cancelCheckOut, cancelCheckIn, ...formActions }, dispatch)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeActionsToProps
)(EventView);
